import ensureError from "@-/com.npmjs.ensure-error";
import { Platform, View } from "@-/com.npmjs.react-native";
import { useReducedMotion } from "@-/com.npmjs.react-native-reanimated";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { registerWelcomeGalaxyExitCapturer } from "@-/dev.improve.haniverse.routes.nav.app.auth.welcome-galaxy-entry";
import { type PropsWithChildren, useEffect, useRef } from "react";
import { isWelcomeGalaxySkiaDisabled } from "#is-welcome-galaxy-skia-disabled";

/** Picturing the screen takes a frame or two; a sign-out never waits longer than this for it. */
const CAPTURE_DEADLINE_MS = 1500;

/**
 * Wraps the app's routes in the view a sign-out pictures (`captureWelcomeGalaxyExit`), so the
 * welcome galaxy the user lands on can launch out of the very screen they left: the picture blurs
 * over where it is, never resized, and fades out as the galaxy comes in beneath it, which then
 * zooms out to the welcome page. Mount once above the routes (`NavLayout`). Under reduced motion,
 * or in builds without Skia, or on web (where Skia cannot picture a native view), it registers
 * nothing, and the welcome page simply starts on its flight.
 */
export function WelcomeGalaxyExitCapture({ children }: PropsWithChildren) {
  const reduceMotion = useReducedMotion();
  const viewRef = useRef<View>(null);

  useEffect(() => {
    if (reduceMotion || Platform.OS === "web" || isWelcomeGalaxySkiaDisabled()) {
      return;
    }
    return registerWelcomeGalaxyExitCapturer(async () => {
      try {
        return await withDeadline(
          { deadline: CAPTURE_DEADLINE_MS, operation: "picture the app for the welcome galaxy" },
          async () => {
            // This shell mounts before web initialises CanvasKit. Importing Skia here keeps its
            // API out of startup: Skia captures global.CanvasKit when its module is evaluated.
            const { captureViewImage } = await import("@-/dev.improve.haniverse.lib.view-image");
            return captureViewImage(viewRef);
          },
        );
      } catch (error) {
        // Only a nicer exit: without a picture the welcome page starts on its flight.
        logger(import.meta.location).warn(
          "[welcome-galaxy] could not picture the app for sign-out",
          ensureError(error),
        );
        return null;
      }
    });
  }, [reduceMotion]);

  return (
    <View className="flex-1" collapsable={false} ref={viewRef}>
      {children}
    </View>
  );
}
